// Minimal error boundary for the Suspense data-loading path: when a corpus
// fetch (unwrapped via React 19 `use()`) rejects, the thrown error lands here
// instead of blanking the app. Error boundaries must be class components — this
// is the one place that requires it. Keep it generic; callers pass a fallback.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered when a child throws; receives the error and a reset callback. */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /** Optional side-effect (e.g. logging) when an error is caught. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return this.props.children;
  }
}
